import { useState, useEffect, lazy, Suspense } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { readInvitation, hasGiftRegistry } from '../lib/invitation';
const AboveTheClouds = lazy(() => import('../components/invitations/AboveTheClouds'));
const CeylonHeritage = lazy(() => import("../components/invitations/CeylonHeritage"));
const AraliyaCourtyard = lazy(() => import("../components/invitations/AraliyaCourtyard"));
const LotusPoruwa = lazy(() => import("../components/invitations/LotusPoruwa"));
const RoyalKandyanArrival = lazy(() => import("../components/invitations/RoyalKandyanArrival"));
const CelestialVows = lazy(() => import('../components/invitations/CelestialVows'));
const TuscanSunset = lazy(() => import('../components/invitations/TuscanSunset'));
const ChampagneReverie = lazy(() => import('../components/invitations/ChampagneReverie'));
const MidnightGarden = lazy(() => import('../components/invitations/MidnightGarden'));
const TimelessIvory = lazy(() => import('../components/invitations/TimelessIvory'));
import { Phone, MessageSquare, Facebook, Gift } from 'lucide-react';
import RSVPForm from '../components/RSVPForm';
const InvitationRSVPForm = lazy(() => import('../components/invitations/InvitationRSVPForm'));
import InvitationAnimation from '../components/InvitationAnimation';
import { supabase } from '../lib/supabase';
import { ThemeProvider } from '../components/ThemeProvider';
import ThemedLayout from '../components/ThemedLayout';
import type { Registry, RegistryEvent } from '../types/registry';
import type { ThemeConfig } from '../types/theme';

export default function RSVPPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [ownerPreview, setOwnerPreview] = useState(false);
  const [itemCount, setItemCount] = useState(0);
  const [giftsLoading, setGiftsLoading] = useState(false);
  const [giftsError, setGiftsError] = useState(false);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventsError, setEventsError] = useState(false);
  const [registry, setRegistry] = useState<Registry | null>(null);
  const [events, setEvents] = useState<RegistryEvent[]>([]);
  const [showAnimation, setShowAnimation] = useState(false);
  const [theme, setTheme] = useState<ThemeConfig>({
    backgroundImage: '',
    textColor: '#ffffff',
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    opacity: 0.75,
    fontFamily: 'IntroRust',
    buttonColor: '#a855f7',
    buttonTextColor: '#ffffff'
  });

  useEffect(() => {
    if (id) {
      loadRegistry();
      loadEvents();
    }
  }, [id, searchParams]);

  useEffect(() => {
    if (!registry) return;

    const invitation = readInvitation(registry.theme?.invitation);
    const previewImage = registry.theme?.backgroundImage || '/invitations/above-the-clouds/cloud-terrace.png';
    const title = `${registry.title} | Giftyfy Gift Registry`;
    const description = registry.description || `View ${registry.title} and choose a thoughtful gift.`;
    document.title = title;

    const metadata = [
      { selector: 'meta[property="og:title"]', attribute: 'property', content: title },
      { selector: 'meta[property="og:description"]', attribute: 'property', content: description },
      { selector: 'meta[property="og:image"]', attribute: 'property', content: previewImage },
      { selector: 'meta[name="twitter:title"]', attribute: 'name', content: title },
      { selector: 'meta[name="twitter:description"]', attribute: 'name', content: description },
      { selector: 'meta[name="twitter:image"]', attribute: 'name', content: previewImage },
    ];

    metadata.forEach(({ selector, attribute, content }) => {
      let element = document.head.querySelector<HTMLMetaElement>(selector);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, selector.includes('og:') ? selector.match(/og:[^"]+/)?.[0] || '' : selector.match(/twitter:[^"]+/)?.[0] || '');
        document.head.appendChild(element);
      }
      element.content = content;
    });

    if (invitation) {
      document.documentElement.lang = 'en';
    }
  }, [registry]);

  const loadRegistry = async () => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const currentUserId = sessionData.session?.user?.id;

      const { data, error } = await supabase
        .from('registries')
        .select('*, theme')
        .eq('id', id)
        .single();

      if (error) throw error;
      if (data) {
        const isOwner = !!currentUserId && currentUserId === data.user_id;
        const preview = isOwner && searchParams.get('preview') === '1' && !!readInvitation(data.theme?.invitation);
        setOwnerPreview(preview);
        if (isOwner && !preview) {
          navigate(`/registries/${id}`, { replace: true });
          return;
        }
        setRegistry(data as unknown as Registry);
        const invitation = readInvitation(data.theme?.invitation);
        if (invitation?.showGiftRegistry && !data.is_closed) {
          await loadGiftAvailability();
        } else {
          setItemCount(0);
          setGiftsError(false);
          setGiftsLoading(false);
        }
        if (data.theme) {
          const defaultTheme = {
            backgroundImage: '',
            textColor: '#ffffff',
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            opacity: 0.75,
            fontFamily: 'IntroRust',
            buttonColor: '#a855f7',
            buttonTextColor: '#ffffff'
          };

          const normalizedTheme = {
            ...defaultTheme,
            ...data.theme,
            backgroundImage: data.theme.backgroundImage || defaultTheme.backgroundImage
          };

          setTheme(normalizedTheme);

          const anim = data.theme.introAnimation || 'none';
          if (anim !== 'none') {
            const sessionKey = `inv-anim-${data.id}`;
            if (!sessionStorage.getItem(sessionKey)) {
              setShowAnimation(true);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error loading registry:', error);
      navigate('/');
    }
  };

  // Classic does not depend on this additional query.
  const loadGiftAvailability = async () => {
    setGiftsLoading(true);
    setGiftsError(false);
    try {
      const { count, error } = await supabase
        .from('registry_items')
        .select('id', { count: 'exact', head: true })
        .eq('registry_id', id);
      if (error) throw error;
      if (count === null) throw new Error('Gift availability could not be confirmed');
      setItemCount(count);
    } catch (error) {
      console.error('Error loading gift availability:', error);
      setGiftsError(true);
    } finally {
      setGiftsLoading(false);
    }
  };

  const loadEvents = async () => {
    setEventsLoading(true); setEventsError(false);
    try {
      const { data, error } = await supabase
        .from('registry_events')
        .select('*')
        .eq('registry_id', id)
        .order('date', { ascending: true });

      if (error) throw error;
      if (data) {
        setEvents(data);
      }
    } catch (error) {
      console.error('Error loading events:', error);
      setEventsError(true);
    } finally { setEventsLoading(false); }
  };

  const handleAnimationComplete = () => {
    if (registry) {
      sessionStorage.setItem(`inv-anim-${registry.id}`, 'played');
    }
    setShowAnimation(false);
  };

  if (!registry) return null;

  const invitation = readInvitation(registry.theme?.invitation);
  if (invitation) {
    const Template = invitation.template === "ceylon-heritage" ? CeylonHeritage : invitation.template === "araliya-courtyard" ? AraliyaCourtyard : invitation.template === "lotus-poruwa" ? LotusPoruwa : invitation.template === "royal-kandyan-arrival" ? RoyalKandyanArrival : invitation.template === "celestial-vows" ? CelestialVows : invitation.template === "tuscan-sunset" ? TuscanSunset : invitation.template === "champagne-reverie" ? ChampagneReverie : invitation.template === "midnight-garden" ? MidnightGarden : invitation.template === "timeless-ivory" ? TimelessIvory : AboveTheClouds;
    const giftsAvailable = !giftsLoading && !giftsError && hasGiftRegistry(invitation, itemCount, !!registry.is_closed);
    return <Suspense fallback={<p className="p-8 text-center">Opening your invitation…</p>}>
      {ownerPreview && <div className="bg-rose-50 p-3 text-center text-sm">Owner preview · RSVP submissions are disabled. <button className="underline" onClick={() => navigate(`/registries/${id}`)}>Back to your registry</button></div>}
      <Template registry={registry} events={events} config={invitation} preview={ownerPreview} giftRegistryAvailable={giftsAvailable} onViewGifts={() => navigate(`/registries/${id}`)}>
        {giftsLoading ? <p>Loading invitation…</p> : giftsError ? <div role="alert"><p>Unable to confirm gift registry availability. Please try again.</p><button onClick={loadGiftAvailability}>Retry</button></div> : eventsLoading ? <p>Loading functions…</p> : eventsError ? <div role="alert"><p>Unable to load the functions. Please try again.</p><button onClick={loadEvents}>Retry</button></div> : events.length === 0 ? <p>The hosts are preparing the RSVP details. Please check back soon.</p> : <InvitationRSVPForm compactEvents={invitation.template !== "above-the-clouds"} key={id} registryId={id!} events={events} redirectAfterSubmit={giftsAvailable} immediateRedirect />}
      </Template>
    </Suspense>;
  }

  return (
    <ThemeProvider initialTheme={theme}>
      <ThemedLayout className="min-h-screen flex flex-col">
        {showAnimation && (
          <InvitationAnimation
            animationType={theme.introAnimation || 'none'}
            title={theme.introTitle || registry.title}
            subtitle={theme.introSubtitle}
            theme={theme}
            onComplete={handleAnimationComplete}
          />
        )}
        <div className="flex-1">
          <div className="max-w-3xl mx-auto px-4 py-12">
            <div className="text-center mb-12">
              <h1 className="text-3xl font-bold mb-4">{registry.title}</h1>
              {registry.description && (
                <p className="text-lg opacity-90 mb-8">{registry.description}</p>
              )}
            </div>

            <RSVPForm registryId={id!} events={events} />

            <div className="mt-8 text-center">
              <p className="text-sm opacity-60 mb-3">Already RSVPed or want to browse gifts first?</p>
              <button
                onClick={() => navigate(`/registries/${id}`)}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-white/20 bg-white/10 hover:bg-white/20 transition-colors text-sm font-medium backdrop-blur-sm"
              >
                <Gift className="w-4 h-4" />
                Skip to Gift Registry
              </button>
            </div>
          </div>
        </div>

        <footer className="relative mt-auto py-8 bg-white/60 backdrop-blur-xl border-t border-white/30 z-10">
          <div className="container mx-auto px-4">
            <div className="flex flex-col items-center space-y-6">
              <img src="/blue-no-logo.png" alt="Giftyfy" className="h-20" style={{ mixBlendMode: 'multiply' }} />
              <p className="text-gray-600 font-poppins">
                Making gift-giving delightful and memorable
              </p>
              <div className="flex flex-col sm:flex-row items-center gap-6">
                <a
                  href="tel:+94779257975"
                  className="relative z-20 inline-flex items-center px-4 py-2 rounded-lg glass-effect hover:bg-white/30 transition-colors text-gray-700 hover:text-gray-900 cursor-pointer font-poppins"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  <span>+94 779257975</span>
                </a>
                <a
                  href="https://wa.me/94779257975"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="relative z-20 inline-flex items-center px-4 py-2 rounded-lg glass-effect hover:bg-white/30 transition-colors text-gray-700 hover:text-gray-900 cursor-pointer font-poppins"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  <span>WhatsApp</span>
                </a>
                <a
                  href="https://web.facebook.com/GiftyfyOfficial"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="relative z-20 inline-flex items-center px-4 py-2 rounded-lg glass-effect hover:bg-white/30 transition-colors text-gray-700 hover:text-gray-900 cursor-pointer font-poppins"
                >
                  <Facebook className="w-4 h-4 mr-2" />
                  <span>Facebook</span>
                </a>
              </div>
              <p className="text-sm text-gray-500 font-poppins">
                &copy; {new Date().getFullYear()} Giftyfy. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </ThemedLayout>
    </ThemeProvider>
  );
}
